import type { LexData } from "./types";
import { buildBalanceOverview } from "./balance";
import { buildParticipationLeaderboard } from "./participation";
import { buildTransparencyRegister } from "./transparency";

export interface CsvReport {
  filename: string;
  rows: string[][];
}

function toCsv(rows: string[][]) {
  return rows.map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(",")).join("\n");
}

export function buildBalanceReport(data: LexData): CsvReport {
  const overview = buildBalanceOverview(data);
  const rows: string[][] = [["Firm", "Jurisdiction", "Own weight", "Delegated weight", "Controlled weight", "Share %", "Status"]];
  for (const firm of overview.byFirm) {
    rows.push([firm.label, firm.detail, firm.ownWeight.toFixed(3), firm.delegatedWeight.toFixed(3), firm.controlledWeight.toFixed(3), (firm.share * 100).toFixed(2), firm.level]);
  }
  return { filename: "balance-report.csv", rows };
}

export function buildParticipationReport(data: LexData): CsvReport {
  const leaderboard = buildParticipationLeaderboard(data);
  const rows: string[][] = [["Firm", "Jurisdiction", "Category", "Discussions", "Contributions", "Amendments", "Votes", "Validated points", "Voting weight"]];
  for (const firm of leaderboard) {
    rows.push([
      firm.firmName,
      firm.jurisdiction,
      firm.membershipCategory,
      String(firm.discussionsStarted),
      String(firm.comments),
      String(firm.amendmentsSponsored),
      String(firm.votesCast),
      String(firm.validatedPoints),
      firm.totalWeight.toFixed(2),
    ]);
  }
  return { filename: "participation-report.csv", rows };
}

export function buildDecisionRegisterReport(data: LexData): CsvReport {
  const register = buildTransparencyRegister(data);
  const rows: string[][] = [["Date", "Type", "Title", "Detail", "Outcome"]];
  for (const entry of register.entries) {
    rows.push([new Date(entry.occurredAt).toISOString().slice(0, 10), entry.kind, entry.title, entry.detail, entry.outcome]);
  }
  return { filename: "decision-register.csv", rows };
}

export function downloadCsv(report: CsvReport) {
  if (typeof document === "undefined") return;
  const blob = new Blob([toCsv(report.rows)], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = report.filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
